import { DocumentUploadOptions, documentUploadForm, uploadProgressHandler } from './portal';
import { apiClient } from './client';
import { extractBlobErrorMessage, getPdfBlob } from './payments';
import { PaginatedResponse } from '../types/pagination';
import {
  CreateStudentPayload,
  DocumentFieldForm,
  DocumentTemplateKey,
  EditableDocumentTemplateKey,
  Invoice,
  InvoiceType,
  InvoicePreview,
  StudentDocument,
  StudentDocumentChecklist,
  StudentListItem,
  StudentTimelineEntry,
  UpdateStudentPayload,
} from '../types/student';
import { PaymentChecklist } from '../types/payment';
import { ChangeRequestForReview, ChangeRequestStatus, PortalInviteRecord } from '../types/portal';

export interface ListStudentsParams {
  page: number;
  pageSize: number;
  search?: string;
  branchId?: number;
  countryId?: number;
  documentsPending?: boolean;
  // Admission Officer automatic-visibility (§1/§2/§11): registered +
  // registration-payment-approved is the trigger, never a manually
  // maintained flag — see students.service.ts's
  // getRegistrationPaymentApprovedStudentIds.
  registrationPaymentApproved?: boolean;
  preferredUniversityId?: number;
  preferredProgramId?: number;
  // Students registered by this counselor (Registered By); ignored by the
  // server for a Counselor, who always sees their own students.
  counselorId?: number;
}

export interface CounselorOption {
  id: number;
  firstName: string;
  lastName: string;
}

/** Active Counselors for the Students page's Counselor filter. */
export const listStudentCounselors = async (branchId?: number): Promise<CounselorOption[]> => {
  const { data } = await apiClient.get('/students/counselors', { params: { branchId } });
  return data.data;
};

/** The Students list as Excel or PDF, with the same filters as the page. */
export const exportStudents = async (
  params: Omit<ListStudentsParams, 'page' | 'pageSize'> & { format: 'xlsx' | 'pdf' },
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const response = await apiClient.get('/students/export', { params, responseType: 'blob' });
    const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return { blob: response.data, filename: match?.[1] ?? `students.${params.format}` };
  } catch (err) {
    throw new Error(await extractBlobErrorMessage(err, "Couldn't export the students list"));
  }
};

export const listStudents = async (
  params: ListStudentsParams,
): Promise<PaginatedResponse<StudentListItem>> => {
  const { data } = await apiClient.get('/students', { params });
  return data;
};

export const getStudent = async (id: number): Promise<StudentListItem> => {
  const { data } = await apiClient.get(`/students/${id}`);
  return data.data;
};

export const createStudent = async (payload: CreateStudentPayload): Promise<StudentListItem> => {
  const { data } = await apiClient.post('/students', payload);
  return data.data;
};

export const updateStudent = async (id: number, payload: UpdateStudentPayload): Promise<StudentListItem> => {
  const { data } = await apiClient.patch(`/students/${id}`, payload);
  return data.data;
};

export const deleteStudent = async (id: number): Promise<void> => {
  await apiClient.delete(`/students/${id}`);
};

export const finalizeStudent = async (id: number): Promise<StudentListItem> => {
  const { data } = await apiClient.post(`/students/${id}/finalize`);
  return data.data;
};

/** The full "Payment Checklist" — every active PaymentCategory's
 * required/paid/outstanding/status for this student, plus a totals
 * summary. Same underlying check finalizeStudent()'s Registration Fee
 * gate enforces server-side; this is the proactive read for the UI. */
export const getPaymentChecklist = async (id: number): Promise<PaymentChecklist> => {
  const { data } = await apiClient.get(`/students/${id}/payment-checklist`);
  return data.data;
};

export const openStudentApplicationPdf = async (id: number): Promise<void> => {
  const response = await apiClient.get(`/students/${id}/application-pdf`, { responseType: 'text' });
  const win = window.open('', '_blank');
  win?.document.write(response.data as string);
};

export const getStudentTimeline = async (studentId: number): Promise<StudentTimelineEntry[]> => {
  const { data } = await apiClient.get(`/students/${studentId}/timeline`);
  return data.data;
};

export const listStudentDocuments = async (
  studentId: number,
  includeHistory = false,
): Promise<StudentDocument[]> => {
  const { data } = await apiClient.get(`/students/${studentId}/documents`, {
    params: includeHistory ? { includeHistory: 'true' } : undefined,
  });
  return data.data;
};

export const getStudentDocumentChecklist = async (studentId: number): Promise<StudentDocumentChecklist> => {
  const { data } = await apiClient.get(`/students/${studentId}/document-checklist`);
  return data.data;
};

export const uploadStudentDocument = async (
  studentId: number,
  file: File,
  documentTypeId: number,
  expiryDateOrOptions?: string | DocumentUploadOptions,
): Promise<StudentDocument> => {
  const options =
    typeof expiryDateOrOptions === 'string' ? { expiryDate: expiryDateOrOptions } : (expiryDateOrOptions ?? {});
  const { data } = await apiClient.post(
    `/students/${studentId}/documents`,
    documentUploadForm(file, documentTypeId, options),
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: uploadProgressHandler(options.onProgress),
    },
  );
  return data.data;
};

export const deleteStudentDocument = async (documentId: number): Promise<void> => {
  await apiClient.delete(`/students/documents/${documentId}`);
};

export const previewStudentDocumentUrl = async (documentId: number): Promise<{ blob: Blob; mimeType: string }> => {
  const response = await apiClient.get(`/students/documents/${documentId}/preview`, { responseType: 'blob' });
  return { blob: response.data, mimeType: String(response.headers['content-type'] ?? 'application/octet-stream') };
};

export const downloadStudentDocument = async (
  documentId: number,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await apiClient.get(`/students/documents/${documentId}/download`, { responseType: 'blob' });
  const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob: response.data, filename: match?.[1] ?? `document-${documentId}` };
};

export const listExpiringDocuments = async (withinDays = 30): Promise<StudentDocument[]> => {
  const { data } = await apiClient.get('/students/documents/expiring', { params: { withinDays } });
  return data.data;
};

export const verifyStudentDocument = async (
  documentId: number,
  status: 'APPROVED' | 'REJECTED',
  rejectionReason?: string,
): Promise<StudentDocument> => {
  const { data } = await apiClient.post(`/students/documents/${documentId}/verify`, { status, rejectionReason });
  return data.data;
};

/** Document PDF Templates correction: generates (or regenerates) one of the
 * official IMC PDFs for this student from existing CRM data. Regenerating
 * never overwrites the previous version — it's kept, isLatest flips false
 * (see StudentDocument.version/previousVersionId/isLatest). */
export const generateStudentDocumentPdf = async (
  studentId: number,
  templateKey: DocumentTemplateKey,
  options: { fieldValues?: Record<string, string>; saveTemplateDefaults?: boolean } = {},
): Promise<StudentDocument> => {
  const { data } = await apiClient.post(`/students/${studentId}/documents/generate`, { templateKey, ...options });
  return data.data;
};

/** Editable document fields (Phase 91) — the pre-filled "Edit Document
 * Details" form for the University Application Form / Agreement. */
export const getDocumentFieldForm = async (
  studentId: number,
  templateKey: EditableDocumentTemplateKey,
): Promise<DocumentFieldForm> => {
  const { data } = await apiClient.get(`/students/${studentId}/documents/generate/${templateKey}/fields`);
  return data.data;
};

/** Renders the document with unsaved edits and the current signature —
 * stores nothing server-side. */
export const previewGeneratedDocument = async (
  studentId: number,
  templateKey: EditableDocumentTemplateKey,
  fieldValues: Record<string, string>,
): Promise<Blob> => {
  const response = await apiClient.post(
    `/students/${studentId}/documents/generate/preview`,
    { templateKey, fieldValues },
    { responseType: 'blob' },
  );
  return response.data;
};

export const listStudentInvoices = async (studentId: number): Promise<Invoice[]> => {
  const { data } = await apiClient.get(`/students/${studentId}/invoices`);
  return data.data;
};

export const createStudentInvoice = async (
  studentId: number,
  payload:
    | { paymentId?: number; amount: number; currencyId: number; description?: string }
    | { paymentIds: number[]; invoiceType: InvoiceType }
    // Outstanding-amount Formatted Invoice — generates for UNPAID/
    // PARTIALLY PAID Payment Checklist items, no existing Payment record
    // required (the amount is the item's current outstanding balance).
    | { paymentCategoryIds: number[]; invoiceType: InvoiceType; exchangeRateId?: number },
): Promise<Invoice> => {
  const { data } = await apiClient.post(`/students/${studentId}/invoices`, payload);
  return data.data;
};

/** Phase 104 — an invoice's figures (rate, lines, totals), nothing saved. */
export const previewStudentInvoice = async (
  studentId: number,
  payload: { paymentCategoryIds: number[]; invoiceType: InvoiceType; exchangeRateId?: number },
): Promise<InvoicePreview> => {
  const { data } = await apiClient.post(`/students/${studentId}/invoices/preview`, payload);
  return data.data;
};

export const downloadInvoicePdf = async (studentId: number, invoiceId: number): Promise<Blob> => {
  const response = await apiClient.get(`/students/${studentId}/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
  return response.data;
};

/** Formatted USD/LKR Invoice — non-download blob fetch for the shared
 * <DocumentPreviewDialog/> (same reasoning as getReceiptBlob/getInvoiceBlob
 * in api/payments.ts: must be fetched as a Blob and never opened via
 * window.open on a blob: URL from a different tab context). */
export const getStudentInvoicePdfBlob = (studentId: number, invoiceId: number) =>
  getPdfBlob(`/students/${studentId}/invoices/${invoiceId}/pdf`, 'Failed to load the invoice');

// Student Portal (Phase 67/68) — staff-side: provisioning via email/WhatsApp/
// manual link, the invitation history, and the Counselor review queue for
// pending change requests.
export const sendPortalInviteEmail = async (studentId: number): Promise<PortalInviteRecord> => {
  const { data } = await apiClient.post(`/students/${studentId}/portal-invite`);
  return data.data;
};

export const sendPortalInviteWhatsApp = async (studentId: number): Promise<PortalInviteRecord> => {
  const { data } = await apiClient.post(`/students/${studentId}/portal-invite/whatsapp`);
  return data.data;
};

export const getPortalInviteLink = async (studentId: number): Promise<{ link: string; invite: PortalInviteRecord }> => {
  const { data } = await apiClient.post(`/students/${studentId}/portal-invite/link`);
  return data.data;
};

export const getPortalInviteHistory = async (studentId: number): Promise<PortalInviteRecord[]> => {
  const { data } = await apiClient.get(`/students/${studentId}/portal-invite/history`);
  return data.data;
};

export const listChangeRequests = async (status?: ChangeRequestStatus): Promise<ChangeRequestForReview[]> => {
  const { data } = await apiClient.get('/students/change-requests', { params: { status } });
  return data.data;
};

export const decideChangeRequest = async (
  requestId: number,
  status: 'APPROVED' | 'REJECTED',
  decisionReason?: string,
): Promise<ChangeRequestForReview> => {
  const { data } = await apiClient.post(`/students/change-requests/${requestId}/decide`, { status, decisionReason });
  return data.data;
};

/** Official dropout / withdrawal (Management Reports) — management or
 * admissions staff only; counted by the Dropout Analysis report. */
export const recordStudentDropout = async (studentId: number, body: { droppedOutAt: string; reason: string }) => {
  const { data } = await apiClient.post(`/students/${studentId}/dropout`, body);
  return data.data;
};

export const reverseStudentDropout = async (studentId: number, reason: string) => {
  await apiClient.delete(`/students/${studentId}/dropout`, { data: { reason } });
};
